import ResultService from './result.service'
import { CT_PICK_3 } from '@/constants/lotto'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ScheduleEntry {
  dow: number[]
  drawTime: string[]
}

interface Schedule {
  [mainSlug: string]: ScheduleEntry
}

interface CachedResult {
  game: string
  draw_name: string
  draw_date: string
  results: any[]
  double_chance_results?: any[]
}

// ---------------------------------------------------------------------------
// Slug / typeGame mappings
// ---------------------------------------------------------------------------

const SLUG_TO_SHORT: Record<string, string> = {
  'loto-590': '590',
  'lotto-6-45': '645',
  'loto-4-59': '459',
  'lotto-5-45': '545',
  'pick-3-lotto-guinee': 'pick3',
}

const SLUG_TO_TYPE_GAME: Record<string, string> = {
  'loto-590': 'multi-lotto',
  'lotto-6-45': 'pari-mutuel-lotto',
  'loto-4-59': 'pari-mutuel-lotto',
  'lotto-5-45': 'pari-mutuel-lotto',
  'pick-3-lotto-guinee': 'fixed-daily-lotto',
}

const TYPE_GAME_SLUGS: Record<string, string[]> = {
  'multi-lotto': ['loto-590'],
  'pari-mutuel-lotto': ['lotto-6-45', 'loto-4-59', 'lotto-5-45'],
  'fixed-daily-lotto': ['pick-3-lotto-guinee'],
}

// Subgame slugs that map to a main slug
const SUBGAME_TO_MAIN: Record<string, string> = {
  'turbo-2': 'loto-590',
  'turbo-3': 'loto-590',
  'turbo-4': 'loto-590',
  'turbo-5': 'loto-590',
  'nap-2': 'loto-590',
  'nap-3': 'loto-590',
  'nap-4': 'loto-590',
  'nap-5': 'loto-590',
  'chance-3': 'loto-590',
  'chance-4': 'loto-590',
  'chance-5': 'loto-590',
  'extra-6': 'loto-590',
  'extra-7': 'loto-590',
  // API-format subgame slugs (lotto-guinee-5-90-* variants)
  'lotto-guinee-5-90-turbo2': 'loto-590',
  'lotto-guinee-5-90-turbo3': 'loto-590',
  'lotto-guinee-5-90-turbo4': 'loto-590',
  'lotto-guinee-5-90-turbo5': 'loto-590',
  'lotto-guinee-5-90-nap2': 'loto-590',
  'lotto-guinee-5-90-nap3': 'loto-590',
  'lotto-guinee-5-90-nap4': 'loto-590',
  'lotto-guinee-5-90-nap5': 'loto-590',
  'lotto-guinee-5-90-chance3': 'loto-590',
  'lotto-guinee-5-90-chance4': 'loto-590',
  'lotto-guinee-5-90-chance5': 'loto-590',
  'lotto-guinee-5-90-extra6': 'loto-590',
  'lotto-guinee-5-90-extra7': 'loto-590',
}

// Path → typeGame mapping for tile-click warming
const PATH_TO_TYPE_GAME: Record<string, string> = {
  '/loto': 'multi-lotto',
  '/loto-645': 'pari-mutuel-lotto',
  '/loto-459': 'pari-mutuel-lotto',
  '/loto-545': 'pari-mutuel-lotto',
  [CT_PICK_3.URL_PATH]: 'fixed-daily-lotto',
}

// ---------------------------------------------------------------------------
// In-flight dedup map
// ---------------------------------------------------------------------------

const inFlight: Map<string, Promise<void>> = new Map()

// ---------------------------------------------------------------------------
// localStorage readers / writers
// ---------------------------------------------------------------------------

export function readLastResult(slug: string): CachedResult | null {
  const shortSlug = SLUG_TO_SHORT[slug]
  if (!shortSlug) return null
  const raw = localStorage.getItem(`result-${shortSlug}`)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function readSchedule(slug: string): ScheduleEntry | null {
  const typeGame = SLUG_TO_TYPE_GAME[slug]
  if (!typeGame) return null
  const raw = localStorage.getItem(`schedule-${typeGame}`)
  if (!raw) return null
  try {
    const map: Schedule = JSON.parse(raw)
    return map[slug] || null
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// computeLastCompletedDraw — pure date math
// ---------------------------------------------------------------------------

export function computeLastCompletedDraw(schedule: ScheduleEntry, now: Date): Date | null {
  if (!schedule || !schedule.dow.length || !schedule.drawTime.length) return null

  // Parse draw times into [hours, minutes] pairs, sorted descending
  const times = schedule.drawTime
    .map(t => {
      const [h, m] = t.split(':').map(Number)
      return { h, m }
    })
    .sort((a, b) => b.h * 60 + b.m - (a.h * 60 + a.m))

  // Walk backwards from today up to 7 days
  for (let dayOffset = 0; dayOffset <= 7; dayOffset++) {
    const candidate = new Date(now.getTime())
    candidate.setUTCDate(candidate.getUTCDate() - dayOffset)
    const dow = candidate.getUTCDay()

    if (!schedule.dow.includes(dow)) continue

    for (const { h, m } of times) {
      const drawDate = new Date(Date.UTC(
        candidate.getUTCFullYear(),
        candidate.getUTCMonth(),
        candidate.getUTCDate(),
        h,
        m,
      ))
      if (drawDate <= now) return drawDate
    }
  }

  return null
}

// ---------------------------------------------------------------------------
// isLastResultFresh — staleness predicate
// ---------------------------------------------------------------------------

export function isLastResultFresh(slug: string, now?: Date): boolean {
  const cached = readLastResult(slug)
  if (!cached) return false
  const schedule = readSchedule(slug)
  if (!schedule) return false
  const lastCompletedDrawTime = computeLastCompletedDraw(schedule, now || new Date())
  if (!lastCompletedDrawTime) return false
  return new Date(cached.draw_date) >= lastCompletedDrawTime
}

// ---------------------------------------------------------------------------
// getLastResultBlock — print-time reader
// ---------------------------------------------------------------------------

export function getLastResultBlock(slug: string | null, now?: Date): CachedResult | null {
  if (!slug) return null
  if (!isLastResultFresh(slug, now)) return null
  const cached = readLastResult(slug)
  if (!cached) return null
  return {
    game: cached.game,
    draw_name: cached.draw_name,
    draw_date: cached.draw_date,
    results: cached.results,
    ...(cached.double_chance_results ? { double_chance_results: cached.double_chance_results } : {}),
  }
}

// ---------------------------------------------------------------------------
// writeScheduleFromGames — normalizer for /games response
// ---------------------------------------------------------------------------

// Game display name → main slug mapping (for events/draws response)
const GAME_NAME_TO_SLUG: Record<string, string> = {
  'loto 5 of 90': 'loto-590',
  'lotto 6 of 45': 'lotto-6-45',
  'loto 4 of 59': 'loto-4-59',
  'lotto 5 of 45': 'lotto-5-45',
  'pick 3': 'pick-3-lotto-guinee',
  'pick 3 lotto guinee': 'pick-3-lotto-guinee',
}

// API game_slug → internal main slug (matched before game name)
const API_GAME_SLUG_TO_SLUG: Record<string, string> = {
  'lotto-guinee-5-90': 'loto-590',
  'lotto-6-45': 'lotto-6-45',
  'loto-4-59': 'loto-4-59',
  'lotto-5-45': 'lotto-5-45',
}

const DOW_TO_NUM: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
}

function formatHM(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute || 0).padStart(2, '0')}`
}

export function writeScheduleFromGames(typeGame: string, resp: any): void {
  if (!resp?.data || !Array.isArray(resp.data)) return

  const slugsForType = TYPE_GAME_SLUGS[typeGame]
  if (!slugsForType) return

  const schedule: Schedule = {}

  for (const entry of resp.data) {
    // Multi-lotto entries are subgames — use parent.slug + parent.config.events.
    // Pari-mutuel / fixed-daily entries use game.slug + game.config.events.
    const gameNode = entry?.game?.parent || entry?.game
    const apiSlug = gameNode?.slug
    if (!apiSlug) continue

    const slug = API_GAME_SLUG_TO_SLUG[apiSlug] || (SLUG_TO_SHORT[apiSlug] ? apiSlug : undefined)
    if (!slug || !slugsForType.includes(slug)) continue
    if (schedule[slug]) continue

    const events = gameNode?.config?.events
    if (!events?.dow || !events?.drawTime) continue

    const dow: number[] = []
    for (const d of events.dow) {
      const n = DOW_TO_NUM[d]
      if (n !== undefined && !dow.includes(n)) dow.push(n)
    }
    if (!dow.length) continue

    const drawTime: string[] = []
    const timeList = Array.isArray(events.drawTime) ? events.drawTime : [events.drawTime]
    const ignoredHours: number[] = Array.isArray(events.ignoredHours) ? events.ignoredHours : []

    if (events.granularity === '+60mins' && timeList.length >= 2) {
      // Hourly window between first and last drawTime, excluding ignoredHours.
      const startH = timeList[0].hour
      const endH = timeList[timeList.length - 1].hour
      const minute = timeList[0].minute || 0
      for (let h = startH; h <= endH; h++) {
        if (ignoredHours.includes(h)) continue
        drawTime.push(formatHM(h, minute))
      }
    } else {
      for (const t of timeList) {
        if (t?.hour === undefined) continue
        const s = formatHM(t.hour, t.minute)
        if (!drawTime.includes(s)) drawTime.push(s)
      }
    }

    if (!drawTime.length) continue

    schedule[slug] = { dow, drawTime }
  }

  localStorage.setItem(`schedule-${typeGame}`, JSON.stringify(schedule))
}

// ---------------------------------------------------------------------------
// warm — fire-and-forget cache warmer
// ---------------------------------------------------------------------------

export function warm(typeGame: string | null): void {
  if (!typeGame) return
  if (inFlight.has(typeGame)) return

  const slugs = TYPE_GAME_SLUGS[typeGame]
  if (!slugs) return

  const apiTypeGame = typeGame === 'fixed-daily-lotto' ? `${typeGame}/api` : typeGame
  const promise = ResultService.getList('', apiTypeGame)
    .then((responseData: any) => {
      const entries = Array.isArray(responseData)
        ? responseData
        : (Array.isArray(responseData?.data) ? responseData.data : [])
      for (const slug of slugs) {
        const shortSlug = SLUG_TO_SHORT[slug]
        if (!shortSlug) continue

        const entry = entries.find((e: any) => {
          if (e?.game_slug) {
            return API_GAME_SLUG_TO_SLUG[e.game_slug] === slug
          }
          if (e?.game === slug) return true
          const mapped = GAME_NAME_TO_SLUG[(e?.game || '').toLowerCase()]
          return mapped === slug
        })
        if (entry) {
          localStorage.setItem(`result-${shortSlug}`, JSON.stringify({
            game: entry.game,
            draw_name: entry.draw_name,
            draw_date: entry.draw_date,
            results: entry.results,
            ...(entry.double_chance_results ? { double_chance_results: entry.double_chance_results } : {}),
          }))
        } else {
          localStorage.removeItem(`result-${shortSlug}`)
        }
      }
    })
    .catch(() => {
      // Swallow — leave previous cache untouched
    })
    .finally(() => {
      inFlight.delete(typeGame)
    }) as Promise<void>

  inFlight.set(typeGame, promise)
}

// ---------------------------------------------------------------------------
// typeGameFor — path → typeGame mapping for onOpenLoto
// ---------------------------------------------------------------------------

export function typeGameFor(path: string): string | null {
  return PATH_TO_TYPE_GAME[path] || null
}

// ---------------------------------------------------------------------------
// slugFor — paramPrint slug → main slug mapping
// ---------------------------------------------------------------------------

export function slugFor(paramSlug: string): string | null {
  if (SLUG_TO_SHORT[paramSlug]) return paramSlug
  if (SUBGAME_TO_MAIN[paramSlug]) return SUBGAME_TO_MAIN[paramSlug]
  return null
}

// ---------------------------------------------------------------------------
// Default export — object façade for Vue component usage
// ---------------------------------------------------------------------------

const LastResultService = {
  warm,
  writeScheduleFromGames,
  getLastResultBlock,
  isLastResultFresh,
  computeLastCompletedDraw,
  readLastResult,
  readSchedule,
  typeGameFor,
  slugFor,
}

export default LastResultService
